import React, { cloneElement, forwardRef, memo, useMemo, useRef } from 'react';
import cn from 'classnames';
import { useTooltipTriggerState } from '@react-stately/tooltip';
import { useTooltip, useTooltipTrigger, } from '@react-aria/tooltip';
import { mergeProps } from '@react-aria/utils';
import { Popper } from '../popper';
import { useForwardedRef, useTypography } from '../../hooks';
import { Box } from '../box';
import { Opacity, TransitionGroup } from '../transitions';
import styles from './styles.module.css';
export const TooltipPopper = memo(forwardRef(function TooltipPopper({ children, className, target, 
// @ts-ignore
state, variant = 'info', invert, arrow: hasArrow = true, autoFlip = true, style: defaultStyle, maxWidth = 300, ...props }, ref) {
    const { tooltipProps } = useTooltip(props, state);
    const { className: typographyClassName } = useTypography({ variant: 'caption1' });
    const arrowRef = useRef(null);
    const style = useMemo(() => ({
        maxWidth,
        ...defaultStyle,
    }), [defaultStyle, maxWidth]);
    const offset = useMemo(() => {
        return ({ popper, placement }) => [
            placement.includes('start')
                ? -popper.width
                : placement.includes('end')
                    ? popper.width
                    : 0,
            placement === 'top' || placement === 'bottom' ? 8 : 6,
        ];
    }, []);
    const arrow = useMemo(() => {
        if (!hasArrow)
            return;
        return {
            ref: arrowRef,
            padding: ({ placement }) => placement.includes('start') || placement.includes('end')
                ? 0
                : { top: 12, bottom: 12, left: 10, right: 10 },
        };
    }, [hasArrow]);
    return (React.createElement(Popper, { Component: Box, className: cn(className, typographyClassName, styles.tooltip, styles[variant], {
            [styles.invert]: invert,
        }), target: target, placement: "top", px: "m", py: "s", style: style, offset: offset, arrow: arrow, autoFlip: autoFlip, ...mergeProps(props, tooltipProps), ref: ref },
        children,
        hasArrow && React.createElement("div", { ref: arrowRef, className: styles.arrow })));
}));
export const Tooltip = memo(forwardRef(function Tooltip({ children, title, opened: defaultOpened, disabled = false, delay = 500, transitionDuration = 200, trigger, onOpen, onClose, autoFlip = true, ...props }, ref) {
    const targetRef = useForwardedRef(children.ref);
    const tooltip = useMemo(() => ({
        isOpen: defaultOpened,
        isDisabled: disabled,
        delay,
        trigger,
        onOpenChange: isOpen => {
            if (isOpen) {
                onOpen === null || onOpen === void 0 ? void 0 : onOpen();
            }
            else {
                onClose === null || onClose === void 0 ? void 0 : onClose();
            }
        },
    }), [defaultOpened, disabled, delay, trigger, onOpen, onClose]);
    const state = useTooltipTriggerState(tooltip);
    const { triggerProps, tooltipProps } = useTooltipTrigger(tooltip, state, targetRef);
    const triggerPropsWithTouch = mergeProps(triggerProps, { onTouchEnd: disabled ? undefined : state.open });
    return (React.createElement(React.Fragment, null,
        cloneElement(children, {
            ...mergeProps(children.props, triggerPropsWithTouch),
            ref: targetRef,
        }),
        React.createElement(TransitionGroup, { on: state.isOpen },
            React.createElement(Opacity, { duration: transitionDuration },
                React.createElement(TooltipPopper, { opened: true, target: targetRef, ...mergeProps(props, tooltipProps), ref: ref, autoFlip: autoFlip, 
                    // @ts-expect-error
                    state: state }, title)))));
}));
